import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnreadMessagesCount } from 'features/chat/hooks';
import { useUnreadOffersCount } from '@hooks/useUnreadOffersCount';
import { DrawerItem } from '../types';

interface UseDrawerItemsProps {
	userRole?: string;
	canSwitchRole?: boolean | string;
	onRoleSwitch: (role: 'farmer' | 'specialist') => void;
	userIsActive?: boolean;
}

export const useDrawerItems = ({
	userIsActive,
	userRole,
	canSwitchRole,
	onRoleSwitch,
}: UseDrawerItemsProps) => {
	const { t } = useTranslation();
	const isFarmer = userRole?.toLowerCase() === 'farmer';
	const { unreadCount } = useUnreadMessagesCount();
	const { unreadOffersCount } = useUnreadOffersCount();

	const specialistDrawerItems = useMemo<DrawerItem[]>(() => {
		const baseItems: DrawerItem[] = [
			{
				id: 1,
				title: t('dashboard'),
				icon: <DashboardOutlinedIcon />,
				link: '/specialist/dashboard',
			},
			{
				id: 2,
				title: t('requests'),
				icon: <ArrowCircleRightOutlinedIcon />,
				link: '/specialist/view-offers',
				badge: {
					count: unreadOffersCount,
					show: unreadOffersCount > 0,
					color: 'error',
				},
			},
			{
				id: 3,
				title: t('chat'),
				icon: <ForumOutlinedIcon />,
				link: '/specialist/chats',
				badge: {
					count: unreadCount,
					show: unreadCount > 0,
					color: 'error',
				},
			},
		];

		// Only add the farmer switch option if the user is active
		if (!userIsActive) {
			baseItems.push({
				id: 4,
				title: isFarmer ? t('goToSpecialistsAccount') : t('specialistsSearch'),
				icon: <SwitchAccountIcon />,
				link: '#',
				onClick: () => onRoleSwitch('farmer'),
				style: {
					color: '#028876',
					padding: '12px 0px 12px 8px !important',
					backgroundColor: '#FFF',
					borderRadius: '8px',
				},
				iconStyle: {
					color: '#028876 !important',
				},
			});
		}

		return baseItems;
	}, [t, isFarmer, onRoleSwitch, userIsActive, unreadCount, unreadOffersCount]);

	const farmerDrawerItems = useMemo<DrawerItem[]>(() => {
		const baseItems: DrawerItem[] = [
			{
				id: 1,
				title: t('dashboard'),
				icon: <DashboardOutlinedIcon />,
				link: '/farmer/dashboard',
			},
			// My Farm item hidden
			// {
			// 	id: 2,
			// 	title: t('myFarm.myFarm'),
			// 	icon: <MapsHomeWorkOutlinedIcon />,
			// 	link: '/farmer/my-farm',
			// },
			{
				id: 2,
				title: t('services'),
				icon: <BusinessCenterIcon />,
				link: '/farmer/services',
			},
			{
				id: 3,
				title: t('offers'),
				icon: <ArrowCircleRightOutlinedIcon />,
				link: '/farmer/view-offers',
				badge: {
					count: unreadOffersCount,
					show: unreadOffersCount > 0,
					color: 'error',
				},
			},
			{
				id: 4,
				title: t('chat'),
				icon: <ForumOutlinedIcon />,
				link: '/farmer/chats',
				badge: {
					count: unreadCount,
					show: unreadCount > 0,
					color: 'error',
				},
			},
		];

		// Only add the specialist switch option if canSwitchRole is true
		if (canSwitchRole) {
			baseItems.push({
				id: 5,
				title: t('goToSpecialistsAccount'),
				icon: <SwitchAccountIcon />,
				link: '#',
				onClick: () => onRoleSwitch('specialist'),
				hideFromMobile: true, // Explicitly hide from mobile navigation
				style: {
					color: '#028876',
					backgroundColor: '#FFF',
					padding: '12px 0px 12px 8px !important',
					borderRadius: '8px',
				},
				iconStyle: {
					color: '#028876 !important',
				},
			});
		}

		return baseItems;
	}, [t, onRoleSwitch, canSwitchRole, unreadCount, unreadOffersCount]);

	return {
		drawerItems: isFarmer ? farmerDrawerItems : specialistDrawerItems,
		isFarmer,
	};
};
